// Period 2 Empires quiz fixture (~1450–1750).
// POC data — not persisted, no grading beyond client-side tally.

const ALL_EMPIRES = [
  'ottoman',
  'safavid',
  'mughal',
  'ming_qing',
  'tokugawa',
  'russian',
  'spanish',
  'portuguese',
  'songhai',
];

export default {
  slug: 'period-2-empires',
  title: 'Period 2 Empires (1450–1750)',
  course_context: 'AP World History: Modern',
  geo_source: 'world_period_2',
  questions: [
    {
      id: 'q1',
      type: 'map_identify',
      prompt:
        'Click the empire that ruled Constantinople and the eastern Mediterranean throughout this period.',
      selectable_entity_ids: ALL_EMPIRES,
      correct_entity_id: 'ottoman',
      explanation:
        "The Ottoman Empire captured Constantinople in 1453 and controlled Anatolia, the Balkans, the Levant, Egypt, and North Africa throughout this period. Istanbul sat at the crossroads of European, Asian, and African trade — making the Ottomans one of the wealthiest and most strategically positioned early-modern states.",
    },
    {
      id: 'q2',
      type: 'map_identify',
      prompt: 'Click the Shia Islamic gunpowder empire that controlled Persia.',
      selectable_entity_ids: ALL_EMPIRES,
      correct_entity_id: 'safavid',
      explanation:
        "The Safavid Empire (1501–1736) established Twelver Shi'ism as the state religion of Persia, distinguishing itself sharply from its Sunni Ottoman and Mughal neighbors. Shah Abbas I modernized the army with gunpowder weapons and made Isfahan a renowned center of art, architecture, and trade.",
    },
    {
      id: 'q3',
      type: 'map_identify',
      prompt:
        'Click the empire whose silver mines at Potosí financed European trade with Asia.',
      selectable_entity_ids: ALL_EMPIRES,
      correct_entity_id: 'spanish',
      explanation:
        "Silver from Potosí (in modern Bolivia) and from mines in Mexico flowed through Spain and onward — via the Manila Galleons to China, and through European banks — reshaping global trade. Spanish silver became the de facto global currency and fueled a commercial revolution that had effects from Ming China to the Ottoman Empire.",
    },
    {
      id: 'q4',
      type: 'map_mcq',
      prompt:
        'The three highlighted empires are commonly grouped together because they:',
      highlighted_entity_ids: ['ottoman', 'safavid', 'mughal'],
      options: [
        {
          id: 'a',
          text: 'Shared a common language across their ruling classes',
          correct: false,
        },
        {
          id: 'b',
          text: 'Were Islamic land-based empires that used gunpowder weapons to consolidate power',
          correct: true,
        },
        { id: 'c', text: 'Were all founded after 1700', correct: false },
        {
          id: 'd',
          text: 'Rejected all maritime trade as religiously forbidden',
          correct: false,
        },
      ],
      explanation:
        "The Ottomans, Safavids, and Mughals are the classic 'Islamic Gunpowder Empires.' Each consolidated power over a large land area by combining cavalry traditions with the disciplined use of firearms and siege artillery — enabling them to centralize authority in ways earlier Eurasian empires could not.",
    },
    {
      id: 'q5',
      type: 'map_mcq',
      prompt: 'The 1494 Treaty of Tordesillas between the two highlighted powers:',
      highlighted_entity_ids: ['spanish', 'portuguese'],
      options: [
        {
          id: 'a',
          text: "Divided newly 'discovered' non-Christian lands along a meridian — granting Portugal claim to Brazil and Spain claim to most of the Americas",
          correct: true,
        },
        {
          id: 'b',
          text: 'Established a military alliance against the expanding Ottoman Empire',
          correct: false,
        },
        {
          id: 'c',
          text: 'Ended the transatlantic slave trade by mutual agreement',
          correct: false,
        },
        {
          id: 'd',
          text: 'Created a shared currency used across both empires',
          correct: false,
        },
      ],
      explanation:
        "The Treaty of Tordesillas (1494), brokered by Pope Alexander VI, drew a line of demarcation roughly 370 leagues west of the Cape Verde Islands. Lands to the east went to Portugal (giving it claim to Brazil and a foothold in Africa and Asia); lands to the west went to Spain (most of the Americas). The treaty framed European imperial claims for more than a century.",
    },
    {
      id: 'q6',
      type: 'map_short_answer',
      prompt:
        "In 2–3 sentences, briefly explain why the highlighted empire largely turned away from overseas expansion after Zheng He's voyages, despite having the technological capability to continue.",
      highlighted_entity_ids: ['ming_qing'],
      model_answer:
        "After Zheng He's seven voyages (1405–1433), Ming officials redirected resources toward defending the northern frontier against Mongol threats and reconstructing the Grand Canal. Confucian scholar-officials came to view expensive maritime ventures as culturally peripheral and diplomatically unnecessary, preferring the tributary system and land-based agrarian priorities. Private overseas trade was officially restricted, and the great treasure fleets were dismantled.",
    },
  ],
};
